import type { ElementRef, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, inject, Input, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import type { PaginationInstance } from 'ngx-pagination';

import { ErrorHandlerService } from '../../../shared/services/error-handler.service';
import { FmLoadingPageService } from '../../../shared/services/fm-loading-page.service';
import { UserService } from '../../../shared/services/user.service';
import { CancelExchangeService } from '../../services/cancel-exchange.service';
import { CommittedExchangeService } from '../../services/committed-exchange.service';
import type { CancelExchangeResponse } from '../../services/interfaces/cancel-exchange-response';
import type { CommitExchangeResponse } from '../../services/interfaces/commit-exchange-response';
import type { CommittedExchangesResponse } from '../../services/interfaces/committed-exchanges-response';
import { PassdataService } from '../../services/passdata.service';
import { RecombinationsService } from '../../services/recombinations.service';
import type { RecombinationRequest } from '../../services/interfaces/ListRecombinationsRequest';
import { RcrExchangeNavigationServiceService } from '../../services/rcr-exchange-navigation-service.service';
import type {
  Dealer,
  ListOfDealerTraderResponse,
  Trader,
} from '../../services/interfaces/list-dealer-trader-response';
import { UntypedFormBuilder, UntypedFormControl, type UntypedFormGroup } from '@angular/forms';
import { ToggleFeature } from '../../../shared/entitlement/toggle-feature';
import { FeatureFlag } from '../../../common/enums/feature-flag.enum';

@Component({
  standalone: false,
  selector: 'invd-committed-exchanges-listings',
  templateUrl: './committed-exchanges-listings.component.html',
  styleUrls: ['./committed-exchanges-listings.component.scss'],
})
export class CommittedExchangesListingsComponent implements OnInit {
  @Input() messageTitle = 'Status';
  @Input() messageBody = '';

  public commitExchangesData: any;
  public presentSortedColumn = '';
  public displayPagination = false;
  public dealers: Dealer[] = [];
  public selectedDealer: Dealer | null = null;
  public selectedAssociatedTrader: Trader | null = null;
  public showDealerTraderModal = false;
  public requestExchangeForm!: UntypedFormGroup;
  public exchangeDealNumber!: number;
  public presentSortedColumnIsAscending = false;
  public committedExchangeResponseError = false;
  public statusCode!: number;
  public isConfirmLinkClicked = true;
  public config: PaginationInstance = {
    id: 'committed-exchanges-container',
    itemsPerPage: 50,
    currentPage: 1,
  };
  @ViewChild('exchangeCancelledOnButtonClick')
  exchangeCancelledOnButtonClick!: ElementRef<HTMLButtonElement>;

  private committedExchangeService = inject(CommittedExchangeService);
  private router = inject(Router);
  private rcrService = inject(RecombinationsService);
  private passDataService = inject(PassdataService);
  private cancelExchangeService = inject(CancelExchangeService);
  private rcrNavigationService = inject(RcrExchangeNavigationServiceService);
  private userService = inject(UserService);
  private fmLoadingPageService = inject(FmLoadingPageService);
  private errorHandlerService = inject(ErrorHandlerService);
  private fb = inject(UntypedFormBuilder);
  private cdr = inject(ChangeDetectorRef);

  public cancelExchangeModalWindow: any = {
    show: false,
    commitExchange: {},
    showCancelconfirmation: false,
    CancelconfirmationMessage: '',
  };

  public enableEdit = ToggleFeature.isToggleFeatureEnabled(
    this.userService.toggleFeatures,
    FeatureFlag.EXCHANGE_EDIT_FLAG,
  );

  ngOnInit() {
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    this.isConfirmLinkClicked = true;
    this.getCommittedExchangesData();
    this.getListOfDealersAndTraders();
    this.requestExchangeForm = this.fb.group({
      dealerControl: new UntypedFormControl(),
      traderControl: new UntypedFormControl(),
    });
  }

  public cancelCommitExchange(_commitExchange: any): void {
    this.cancelExchangeModalWindow.show = true;
    this.cancelExchangeModalWindow.commitExchange = _commitExchange;
  }

  public callCancelExchangeService(_commitExchange: any): void {
    this.exchangeCancelledOnButtonClick.nativeElement.disabled = true;
    const cancelExchangeRequestData: any = new Object();
    cancelExchangeRequestData.exchangeConfirmationNumber =
      this.cancelExchangeModalWindow.commitExchange.exchangeConfirmationNumber;
    this.cancelExchangeServiceCall(cancelExchangeRequestData);
  }

  private cancelExchangeServiceCall(request: any) {
    this.cancelExchangeService.cancelExchangeService(request).subscribe({
      next: (data) => this.handleCancelExchangeResponse(data as CancelExchangeResponse),
      error: (error) => this.handleRetrieveExchangeResponseError(error),
    });
  }

  private handleCancelExchangeResponse(cancelExchangeResp: CancelExchangeResponse) {
    this.cancelExchangeModalWindow.show = false;
    this.cancelExchangeModalWindow.showCancelConfirmation = true;
    this.cancelExchangeModalWindow.CancelConfirmationMessage = cancelExchangeResp.displayMessage;
    this.getCommittedExchangesData();
    this.cdr.markForCheck();
  }

  public handleCancelExchangeResponseError(error: any) {
    const errorObject = error.error;
    this.errorHandlerService.setErrorObj(errorObject);
    this.router.navigate(['error']);
  }

  private getCommittedExchangesData(): void {
    this.committedExchangeService.getCommittedExchanges().subscribe({
      next: (data) => this.handleCommittedExchangesResponse(data as CommittedExchangesResponse),
      error: (error) => this.handleCommittedExchangeResponseError(error),
    });
  }

  private handleCommittedExchangesResponse(
    committedExchangesObject: CommittedExchangesResponse,
  ): void {
    this.commitExchangesData =
      committedExchangesObject && committedExchangesObject.committedExchanges;
    this.committedExchangeResponseError = false;
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    if (
      committedExchangesObject &&
      committedExchangesObject.committedExchanges &&
      committedExchangesObject.committedExchanges.length > 50
    ) {
      this.displayPagination = true;
    } else {
      this.displayPagination = false;
      this.config = {
        id: 'committed-exchanges-container',
        itemsPerPage: 50,
        currentPage: 1,
      };
    }
    this.cdr.markForCheck();
  }

  private handleCommittedExchangeResponseError(error: any): void {
    const errorObject = error.error;
    this.committedExchangeResponseError = true;
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.displayPagination = false;
    this.messageBody = errorObject.messages[0].errorMessage;
    this.cdr.markForCheck();
  }

  public sortByGridByColumnName(columnName: string): void {
    if (this.presentSortedColumn !== columnName) {
      this.presentSortedColumnIsAscending = true;
    } else {
      this.presentSortedColumnIsAscending = !this.presentSortedColumnIsAscending;
    }

    const isAscending = this.presentSortedColumnIsAscending ? 1 : -1;

    this.commitExchangesData.sort((a: any, b: any) => {
      const textA = a[columnName];
      const textB = b[columnName];
      return textA < textB ? -1 * isAscending : textA > textB ? 1 * isAscending : 0;
    });

    this.presentSortedColumn = columnName;
  }

  private retrieveExchangeServiceCall(request: any) {
    this.rcrService.retrieveExchangeService(request).subscribe({
      next: (data) => this.handleRetrieveExchangeResponse(data as CommitExchangeResponse),
      error: (error) => this.handleRetrieveExchangeResponseError(error),
    });
  }

  private handleRetrieveExchangeResponse(commitExchangeResp: CommitExchangeResponse) {
    this.passDataService.setCommitExchangeResponse(commitExchangeResp);
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.isConfirmLinkClicked = true;
    this.router.navigate([
      'rcr-exchange/committed-exchanges',
      commitExchangeResp.exchangeConfirmationNumber,
      'confirm',
    ]);
  }

  private handleRetrieveExchangeResponseError(error: any) {
    this.isConfirmLinkClicked = true;
    const errorObject = error.error;
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.errorHandlerService.setErrorObj(errorObject);
    this.router.navigate(['error']);
  }

  public retrieveExchangeData(index: number): void {
    const retrieveExchangeRequestData: any = new Object();
    const exchangeConfirmationNum: number =
      this.commitExchangesData[index].exchangeConfirmationNumber;
    retrieveExchangeRequestData.exchangeConfirmationNumber = exchangeConfirmationNum;
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    if (this.isConfirmLinkClicked) {
      this.retrieveExchangeServiceCall(retrieveExchangeRequestData);
    }

    this.isConfirmLinkClicked = false;
  }

  public editExchange(exchange: any): void {
    const recomRequestData: any = new Object();
    recomRequestData.exchangeConfirmationNumber = exchange.exchangeConfirmationNumber;
    recomRequestData.dealSubType = exchange.dealSubType;
    recomRequestData.takedownFlow = false;
    recomRequestData.userSearchInput = exchange.dealNumber;
    this.exchangeDealNumber = exchange.dealNumber;
    this.rcrService.setRecombinationRequestData(recomRequestData as RecombinationRequest);
    this.passDataService.setExchangeConfirmationNumber(exchange.exchangeConfirmationNumber);
    this.passDataService.setRecombinationDataCache(null);
    this.rcrNavigationService.canNavigate = true;

    if (this.userService.isInternalUser) {
      this.showDealerTraderModal = true;
    } else {
      this.selectedDealer = this.dealers[0];
      this.selectedAssociatedTrader = this.dealers[0].traders[0];
      this.userService.setDealerName(this.selectedDealer.dealerName);
      this.userService.setTrader(
        this.selectedAssociatedTrader.traderFirstName +
          ' ' +
          this.selectedAssociatedTrader.traderLastName,
        this.selectedAssociatedTrader.traderId,
      );
      this.submitTraderDetails();
    }
  }

  private getListOfDealersAndTraders(): void {
    if (this.passDataService.getCustomers() != null) {
      this.dealers = this.passDataService.getCustomers();
      this.fmLoadingPageService.isLoaderRequesting.set(false);
      return;
    }
    this.rcrService.getListDealersTradersService().subscribe({
      next: (data) =>
        this.handleListOfDealersAndTradersResponse(data as ListOfDealerTraderResponse),
      error: (error) => this.handleRetrieveExchangeResponseError(error),
    });
  }

  private handleListOfDealersAndTradersResponse(data: ListOfDealerTraderResponse): void {
    this.dealers = data.dealer;
    this.passDataService.setCustomers(data.dealer);
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public submitTraderDetails(): void {
    this.showDealerTraderModal = false;
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    this.router.navigate(['rcr-exchange', this.exchangeDealNumber, 'enter-balances']);
  }

  public onDealerSelect(dealer: Dealer): void {
    this.selectedDealer = dealer;
    this.selectedAssociatedTrader = null;
    if (this.selectedDealer != null) {
      this.requestExchangeForm.controls.traderControl.reset();
      this.userService.setDealerName(dealer.dealerName);
    }
  }

  protected onTraderSelect(trader: Trader): void {
    this.selectedAssociatedTrader = trader;
    if (trader != null) {
      this.userService.setTrader(
        trader.traderFirstName + ' ' + trader.traderLastName,
        trader.traderId,
      );
    }
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }
}
